/**
 * DatenbasisPage - Christiani.AI Wissensbasis
 * 
 * Ermöglicht das Hochladen und Analysieren von Dokumenten
 * zur Erstellung eines Brand-Profils für Content-Generierung.
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from 'sonner';
import { 
  Upload, FileText, Trash2, Database, Sparkles, Loader2, X,
  Eye, Download, CheckCircle, FileDown, ExternalLink, RefreshCw,
  Zap, Video, Calendar, TrendingUp, Send, Image, Instagram, Facebook, 
  Linkedin, MessageCircle, Play, Copy, Music
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DatenbasisPage = () => {
  // Upload States
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [presets, setPresets] = useState(null);
  
  // Input States
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [manualText, setManualText] = useState('');
  const [manualTitle, setManualTitle] = useState('');
  
  // UI States
  const [activeTab, setActiveTab] = useState('upload');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Load data on mount
  useEffect(() => {
    loadDocuments();
    loadPresets();
  }, []);
  
  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API}/datenbasis/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Dokumente konnten nicht geladen werden');
    }
  };

  const loadPresets = async () => {
    try {
      const response = await axios.get(`${API}/datenbasis/presets`);
      setPresets(response.data.presets);
    } catch (error) {
      toast.error('Presets konnten nicht geladen werden');
    }
  };
  
  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/datenbasis/profile`);
      setProfile(response.data.profile);
      setShowProfileModal(true);
    } catch (error) {
      toast.error('Fehler beim Laden des Profils');
    }
  };
  
  // Quick-Flow: Masterpost direkt aus Datenbasis erstellen
  const createMasterpostFromDatenbasis = async () => {
    try {
      // Lade Profil falls nicht vorhanden
      let currentProfile = profile;
      if (!currentProfile) {
        const response = await axios.get(`${API}/datenbasis/profile`);
        currentProfile = response.data.profile;
      }
      
      if (!currentProfile || !currentProfile.usps?.length) {
        toast.error('Bitte erst Dokumente analysieren, um ein Profil zu erstellen');
        return;
      }
      
      // Generiere Hook aus USPs
      const mainUsp = currentProfile.usps[0];
      const hook = `${mainUsp} 🚀`;
      
      // Generiere Body aus Story-Fundament und Werten
      const story = currentProfile.story_fundament || 'Wir helfen dir, dein Potenzial zu entfalten.';
      const werte = currentProfile.werte?.slice(0, 2).join(' & ') || 'Professionalität & Erfolg';
      const body = `${story}\n\nUnsere Werte: ${werte}\n\n${currentProfile.usps?.slice(1, 3).join(' | ') || ''}`;
      
      // CTA aus Tonalität
      const cta = currentProfile.tonalitaet?.includes('professionell') 
        ? 'Vereinbare jetzt dein kostenloses Erstgespräch!'
        : 'Schreib uns – wir helfen dir gerne! 💬';
      
      // Hashtags aus Keywords
      const hashtags = [
        '#Förderfuchs',
        ...(currentProfile.keywords_jobcenter?.slice(0, 5).map(k => `#${k.replace(/\s+/g, '')}`) || []),
        '#AVGS', '#Coaching', '#Karriere'
      ].slice(0, 10);
      
      // Masterpost erstellen
      const masterpostData = {
        title: `Datenbasis-Post: ${mainUsp.substring(0, 30)}...`,
        target_audience: currentProfile.zielgruppen?.[0] || 'arbeitssuchende',
        hook,
        body,
        cta,
        media_url: null,
        base_hashtags: hashtags,
        channels: ['instagram', 'facebook', 'linkedin']
      };
      
      const response = await axios.post(`${API}/masterpost/create`, masterpostData);
      
      if (response.data.success) {
        toast.success('Masterpost aus Datenbasis erstellt! 🎉');
        // Navigiere zur Masterpost-Seite
        window.location.href = '/masterpost';
      }
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  // OpenClaw TikTok Mode: Generiert TikTok-Slideshow aus Datenbasis
  const [tiktokModal, setTiktokModal] = useState(false);
  const [tiktokContent, setTiktokContent] = useState(null);
  const [tiktokLoading, setTiktokLoading] = useState(false);
  
  // NEU: Larry Format-Mix - 1 Topic → 12 Posts
  const [formatMixModal, setFormatMixModal] = useState(false);
  const [formatMixContent, setFormatMixContent] = useState(null);
  const [formatMixLoading, setFormatMixLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('bewerbung');
  
  const topics = [
    { key: 'bewerbung', label: 'Bewerbung', icon: '📝' },
    { key: 'motivation', label: 'Motivation', icon: '🚀' },
    { key: 'azav', label: 'AZAV-Förderung', icon: '💰' },
    { key: 'gruendung', label: 'Gründung', icon: '💡' },
    { key: 'erfolgsgeschichte', label: 'Erfolgsgeschichte', icon: '✨' }
  ];
  
  const generateFormatMix = async () => {
    setFormatMixLoading(true);
    try {
      const response = await axios.post(`${API}/openclaw/format-mix`, {
        topic: selectedTopic,
        slides: 7
      });
      if (response.data.success) {
        setFormatMixContent(response.data.format_mix);
        setFormatMixModal(true);
        toast.success(`12 Posts für "${selectedTopic}" generiert! 🎉`);
      }
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFormatMixLoading(false);
    }
  };
  
  const generateTikTokContent = async () => {
    setTiktokLoading(true);
    try {
      const response = await axios.post(`${API}/openclaw/generate-from-datenbasis`);
      if (response.data.success) {
        setTiktokContent(response.data.tiktok_content);
        setTiktokModal(true);
        toast.success('TikTok-Slideshow generiert! 🎬');
      }
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTiktokLoading(false);
    }
  };
  
  // File Upload Handler
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', 'general');
        
        const response = await axios.post(`${API}/datenbasis/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success(`${file.name} erfolgreich analysiert!`);
        loadDocuments();
      } catch (error) {
        toast.error(`Fehler bei ${file.name}: ${error.response?.data?.detail || error.message}`);
      }
    }
    
    setUploading(false);
  };
  
  // URL Analysis Handler
  const handleUrlAnalysis = async () => {
    if (!websiteUrl) {
      toast.error('Bitte URL eingeben');
      return;
    }
    
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('url', websiteUrl);
      
      const response = await axios.post(`${API}/datenbasis/analyze-url`, formData);
      toast.success('Website erfolgreich analysiert!');
      setWebsiteUrl('');
      loadDocuments();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Text Analysis Handler
  const handleTextAnalysis = async () => {
    if (!manualText || manualText.length < 50) {
      toast.error('Text zu kurz (min. 50 Zeichen)');
      return;
    }
    
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('text', manualText);
      formData.append('title', manualTitle || 'Manueller Text');
      
      const response = await axios.post(`${API}/datenbasis/analyze-text`, formData);
      toast.success('Text erfolgreich analysiert!');
      setManualText('');
      setManualTitle('');
      loadDocuments();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Download PDF
  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/datenbasis/profile/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'foerderfuchs-marketing-profil.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF heruntergeladen!');
    } catch (error) {
      toast.error('Fehler beim PDF-Download');
    }
  };
  
  // Drag & Drop Handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };
  
  return (
    <div className="space-y-8" data-testid="datenbasis-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-bold text-stone-900">Datenbasis</h1>
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              Christiani.AI Modus
            </span>
          </div>
          <p className="text-stone-600 mt-1">Deine zentrale Wissensdatenbank für KI-gestützte Content-Erstellung</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={loadProfile}
            className="btn-secondary flex items-center gap-2"
          >
            <Eye size={18} />
            Profil anzeigen
          </button>
          <button
            onClick={downloadPDF}
            className="btn-primary flex items-center gap-2"
          >
            <Download size={18} />
            PDF-Report
          </button>
          <button
            onClick={createMasterpostFromDatenbasis}
            data-testid="quick-masterpost-btn"
            className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:from-purple-600 hover:to-blue-600 transition-all shadow-md"
          >
            <Zap size={18} />
            Direkt Masterpost
          </button>
          <button
            onClick={generateTikTokContent}
            data-testid="openclaw-tiktok-btn"
            className="bg-gradient-to-r from-pink-500 to-red-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:from-pink-600 hover:to-red-600 transition-all shadow-md"
          >
            <Video size={18} />
            TikTok Mode
          </button>
        </div>
      </div>
      
      {/* LARRY FORMAT-MIX Section */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-200 p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-2xl">
              🦊
            </div>
            <div>
              <h3 className="font-bold text-stone-900 text-lg">LARRY FORMAT-MIX</h3>
              <p className="text-sm text-stone-600">1 Topic → 12 plattformspezifische Posts</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              className="px-4 py-2 border border-orange-200 rounded-lg bg-white text-stone-700 focus:ring-2 focus:ring-orange-500"
            >
              {topics.map(t => (
                <option key={t.key} value={t.key}>{t.icon} {t.label}</option>
              ))}
            </select>
            
            <button
              onClick={generateFormatMix}
              disabled={formatMixLoading}
              data-testid="larry-format-mix-btn"
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:from-orange-600 hover:to-red-600 transition-all shadow-lg disabled:opacity-50"
            >
              {formatMixLoading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Generiere...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  GENERIEREN
                </>
              )}
            </button>
          </div>
        </div>
        
        <div className="mt-4 flex items-center gap-4 text-sm text-stone-600">
          <span className="flex items-center gap-1"><Instagram size={14} /> Reel+Carousel+Stories</span>
          <span className="flex items-center gap-1"><Facebook size={14} /> Video+Carousel</span>
          <span className="flex items-center gap-1">📊 GMB Infografik</span>
          <span className="flex items-center gap-1"><Linkedin size={14} /> Text+Carousel</span>
        </div>
      </div>
      
      {/* SKILLS Quick-Access */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <button
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/openclaw/skill/social-post`, { topic: selectedTopic, channel: 'linkedin' });
              toast.success('Social Post generiert!');
              navigator.clipboard.writeText(res.data.post?.full_text || '');
              toast.info('Post in Zwischenablage kopiert!');
            } catch (e) { toast.error('Fehler'); }
          }}
          data-testid="skill-social-post-btn"
          className="bg-white rounded-xl border border-stone-200 p-4 hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Send size={16} className="text-blue-600" />
            </div>
            <span className="font-semibold text-stone-900">/social-post</span>
          </div>
          <p className="text-xs text-stone-500">Generiert vollständigen Post für LinkedIn</p>
        </button>
        
        <button
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/openclaw/skill/content-plan`, { topic: selectedTopic });
              toast.success('Content-Plan mit 3 Post-Ideen generiert!');
            } catch (e) { toast.error('Fehler'); }
          }}
          data-testid="skill-content-plan-btn"
          className="bg-white rounded-xl border border-stone-200 p-4 hover:border-purple-300 hover:bg-purple-50 transition-all text-left"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar size={16} className="text-purple-600" />
            </div>
            <span className="font-semibold text-stone-900">/content-plan</span>
          </div>
          <p className="text-xs text-stone-500">Erstellt 3 Post-Ideen mit Scheduling</p>
        </button>
        
        <button
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/openclaw/skill/azav-engage`, { topic: 'AVGS-Förderung', channel: 'instagram' });
              toast.success('AZAV-Engage Post generiert!');
              navigator.clipboard.writeText(res.data.content?.full_post || '');
              toast.info('Post in Zwischenablage kopiert!');
            } catch (e) { toast.error('Fehler'); }
          }}
          data-testid="skill-azav-engage-btn"
          className="bg-white rounded-xl border border-stone-200 p-4 hover:border-orange-300 hover:bg-orange-50 transition-all text-left"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <Zap size={16} className="text-orange-600" />
            </div>
            <span className="font-semibold text-stone-900">/azav-engage</span>
          </div>
          <p className="text-xs text-stone-500">AZAV-fokussierter Engagement Content</p>
        </button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Database size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{documents.length}</p>
              <p className="text-sm text-stone-500">Dokumente</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{documents.filter(d => d.status === 'analyzed').length}</p>
              <p className="text-sm text-stone-500">Analysiert</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{presets?.keywords_jobcenter?.length || 0}</p>
              <p className="text-sm text-stone-500">Keywords</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{presets?.focus_areas?.length || 0}</p>
              <p className="text-sm text-stone-500">Fokus-Bereiche</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Upload & Input */}
        <div className="space-y-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="flex border-b border-stone-200">
              {[
                { key: 'upload', label: 'Dokument hochladen', icon: Upload },
                { key: 'url', label: 'Website analysieren', icon: ExternalLink },
                { key: 'text', label: 'Text eingeben', icon: FileText }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all ${
                    activeTab === tab.key
                      ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500'
                      : 'text-stone-500 hover:bg-stone-50'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>
            
            <div className="p-6">
              {/* Upload Tab */}
              {activeTab === 'upload' && (
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                    dragActive
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-stone-300 hover:border-purple-400'
                  }`}
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
                      <p className="text-stone-600">Analysiere Dokument...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-stone-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-stone-700 mb-2">
                        Dokumente hier ablegen
                      </p>
                      <p className="text-sm text-stone-500 mb-4">
                        PDF, TXT oder DOCX (z.B. AZAV-Richtlinien, Fallstudien)
                      </p>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg cursor-pointer hover:bg-purple-600">
                        <Upload size={16} />
                        Datei auswählen
                        <input
                          type="file"
                          accept=".pdf,.txt,.docx"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                        />
                      </label>
                    </>
                  )}
                </div>
              )}
              
              {/* URL Tab */}
              {activeTab === 'url' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Website-URL</label>
                    <input
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://förderfuchs-bildung.de"
                      className="w-full p-3 border border-stone-200 rounded-lg"
                    />
                  </div>
                  <button
                    onClick={handleUrlAnalysis}
                    disabled={analyzing || !websiteUrl}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Analysiere Website...
                      </>
                    ) : (
                      <>
                        <Search size={18} />
                        Website analysieren
                      </>
                    )}
                  </button>
                  <p className="text-xs text-stone-500 text-center">
                    Tipp: Analysiere deine eigene Website, um Tonalität und USPs zu extrahieren
                  </p>
                </div>
              )}
              
              {/* Text Tab */}
              {activeTab === 'text' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Titel (optional)</label>
                    <input
                      type="text"
                      value={manualTitle}
                      onChange={(e) => setManualTitle(e.target.value)}
                      placeholder="z.B. Über Förderfuchs"
                      className="w-full p-3 border border-stone-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Text</label>
                    <textarea
                      value={manualText}
                      onChange={(e) => setManualText(e.target.value)}
                      placeholder="Füge hier deinen Text ein (z.B. Über-uns Text, Kernbotschaften, USPs...)"
                      rows={6}
                      className="w-full p-3 border border-stone-200 rounded-lg resize-none"
                    />
                  </div>
                  <button
                    onClick={handleTextAnalysis}
                    disabled={analyzing || manualText.length < 50}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Analysiere Text...
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        Text analysieren
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Förderfuchs Presets */}
          {presets && (
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🦊</span>
                <h3 className="font-heading text-lg font-bold">Förderfuchs Presets</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-stone-500 mb-1">Fokus-Bereiche</p>
                  <div className="flex flex-wrap gap-1">
                    {presets.focus_areas?.slice(0, 4).map(area => (
                      <span key={area} className="text-xs px-2 py-1 bg-white rounded-full text-orange-700">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-stone-500 mb-1">Jobcenter-Keywords</p>
                  <div className="flex flex-wrap gap-1">
                    {presets.keywords_jobcenter?.slice(0, 6).map(kw => (
                      <span key={kw} className="text-xs px-2 py-1 bg-orange-100 rounded text-orange-700">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right: Documents List */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <Database size={20} className="text-purple-600" />
              Analysierte Dokumente
            </h2>
            <button
              onClick={loadDocuments}
              className="p-2 hover:bg-stone-100 rounded-lg"
            >
              <RefreshCw size={16} />
            </button>
          </div>
          
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 mb-2">Noch keine Dokumente</p>
              <p className="text-sm text-stone-400">
                Lade PDFs hoch oder analysiere deine Website
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {documents.map((doc, idx) => (
                <div key={idx} className="border border-stone-200 rounded-lg p-4 hover:border-purple-300 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        doc.source === 'url' ? 'bg-blue-100' : 
                        doc.source === 'upload' ? 'bg-purple-100' : 'bg-green-100'
                      }`}>
                        {doc.source === 'url' ? <ExternalLink size={18} className="text-blue-600" /> :
                         doc.source === 'upload' ? <FileText size={18} className="text-purple-600" /> :
                         <FileText size={18} className="text-green-600" />}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 line-clamp-1">{doc.filename}</p>
                        <p className="text-xs text-stone-500">
                          {doc.doc_type} • {new Date(doc.created_at).toLocaleDateString('de-DE')}
                        </p>
                        {doc.analysis && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {doc.analysis.usps?.slice(0, 2).map((usp, i) => (
                              <span key={i} className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded">
                                {usp.substring(0, 25)}...
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      doc.status === 'analyzed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {doc.status === 'analyzed' ? '✓ Analysiert' : 'Ausstehend'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Profile Modal */}
      {showProfileModal && profile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                  🦊 Förderfuchs Marketing-Profil
                </h2>
                <p className="text-sm text-stone-500">
                  Kombiniert aus {profile.doc_count || 0} Dokumenten
                </p>
              </div>
              <button onClick={() => setShowProfileModal(false)} className="p-2 hover:bg-stone-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Story */}
              {profile.story_fundament && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-semibold text-purple-800 mb-2">📖 Story-Fundament</h3>
                  <p className="text-stone-700">{profile.story_fundament}</p>
                </div>
              )}
              
              {/* Tonalität */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-stone-500">Tonalität:</span>
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                  {profile.tonalitaet}
                </span>
              </div>
              
              {/* Werte */}
              {profile.werte?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">💎 Kernwerte</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.werte.map((w, i) => (
                      <span key={i} className="px-3 py-1 bg-stone-100 text-stone-700 rounded-full text-sm">
                        {w}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* USPs */}
              {profile.usps?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">⭐ Unique Selling Points</h3>
                  <ul className="space-y-1">
                    {profile.usps.map((usp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                        {usp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Keywords */}
              {profile.keywords_jobcenter?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">🔑 Jobcenter-Keywords</h3>
                  <div className="flex flex-wrap gap-1">
                    {profile.keywords_jobcenter.map((kw, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Empfehlungen */}
              {profile.empfehlungen?.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h3 className="font-semibold text-amber-800 mb-2">💡 Optimierungsempfehlungen</h3>
                  <ul className="space-y-1">
                    {profile.empfehlungen.map((emp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                        <span className="text-amber-500">→</span>
                        {emp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6 pt-4 border-t">
              <button
                onClick={downloadPDF}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Download size={18} />
                Als PDF herunterladen
              </button>
              <button
                onClick={() => setShowProfileModal(false)}
                className="btn-secondary"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* OpenClaw TikTok Modal */}
      {tiktokModal && tiktokContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-red-500 rounded-xl flex items-center justify-center">
                    <Video size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-stone-900">OpenClaw TikTok Slideshow</h2>
                    <p className="text-sm text-stone-500">LarrySocial Agent • {tiktokContent.slideshow?.slides?.length || 6} Slides</p>
                  </div>
                </div>
                <button onClick={() => setTiktokModal(false)} className="text-stone-400 hover:text-stone-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Slideshow Preview */}
              <div className="bg-stone-50 rounded-xl p-4">
                <h3 className="font-semibold text-stone-900 mb-3">Slideshow Preview</h3>
                <div className="grid grid-cols-3 gap-3">
                  {tiktokContent.slideshow?.slides?.map((slide, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-orange-400 to-pink-500 rounded-lg p-3 text-white aspect-[9/16] flex flex-col justify-center">
                      <span className="text-xs opacity-70 mb-1">Slide {idx + 1}</span>
                      <p className="text-xs font-medium leading-tight">{slide.text?.substring(0, 60)}...</p>
                      <span className="text-xs opacity-70 mt-2">{slide.duration}s</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Hashtags */}
              <div>
                <h3 className="font-semibold text-stone-900 mb-2">Empfohlene Hashtags</h3>
                <div className="flex flex-wrap gap-2">
                  {tiktokContent.slideshow?.hashtags?.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">{tag}</span>
                  ))}
                </div>
              </div>
              
              {/* Music Suggestion */}
              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                <Music size={18} className="text-purple-600" />
                <span className="text-sm text-purple-700">Musik-Empfehlung: {tiktokContent.slideshow?.music_suggestion}</span>
              </div>
              
              {/* Next Steps */}
              <div className="bg-blue-50 rounded-xl p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Nächste Schritte</h3>
                <ol className="text-sm text-blue-700 space-y-1">
                  {tiktokContent.next_steps?.map((step, idx) => (
                    <li key={idx}>{step}</li>
                  ))}
                </ol>
              </div>
              
              {/* Export Options */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-stone-500">Export bereit für:</span>
                {tiktokContent.export_ready?.tiktok && <span className="px-2 py-1 bg-black text-white text-xs rounded">TikTok</span>}
                {tiktokContent.export_ready?.instagram_reels && <span className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded">IG Reels</span>}
                {tiktokContent.export_ready?.youtube_shorts && <span className="px-2 py-1 bg-red-500 text-white text-xs rounded">YT Shorts</span>}
              </div>
            </div>
            
            <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(tiktokContent, null, 2));
                  toast.success('TikTok-Daten kopiert!');
                }}
                className="btn-secondary flex items-center gap-2"
              >
                <Copy size={16} />
                JSON kopieren
              </button>
              <button
                onClick={() => setTiktokModal(false)}
                className="btn-primary"
              >
                Fertig
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* FORMAT-MIX Modal - 4-Kanal Preview */}
      {formatMixModal && formatMixContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-2xl">
                    🦊
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-stone-900">LARRY FORMAT-MIX</h2>
                    <p className="text-sm text-stone-500">
                      {formatMixContent.export?.total_assets || 12} Assets • Topic: {formatMixContent.topic}
                    </p>
                  </div>
                </div>
                <button onClick={() => setFormatMixModal(false)} className="text-stone-400 hover:text-stone-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* 4-Kanal Grid */}
              <div className="grid grid-cols-2 gap-6">
                {/* Instagram */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Instagram size={20} className="text-pink-600" />
                    <h3 className="font-bold text-stone-900">INSTAGRAM</h3>
                    <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded">Reel+Carousel+Stories</span>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Reel Preview */}
                    <div className="bg-white rounded-lg p-3 border border-pink-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Play size={14} className="text-pink-500" />
                        <span className="text-xs font-medium">Reel (15s)</span>
                      </div>
                      <p className="text-xs text-stone-600 line-clamp-2">
                        {formatMixContent.channels?.instagram?.reel?.script?.substring(0, 80)}...
                      </p>
                    </div>
                    
                    {/* Carousel Preview */}
                    <div className="bg-white rounded-lg p-3 border border-pink-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Image size={14} className="text-pink-500" />
                        <span className="text-xs font-medium">Carousel ({formatMixContent.channels?.instagram?.carousel?.length || 7} Slides)</span>
                      </div>
                      <div className="flex gap-1">
                        {formatMixContent.channels?.instagram?.carousel?.slice(0, 4).map((s, i) => (
                          <div key={i} className="w-8 h-12 bg-gradient-to-br from-orange-400 to-pink-400 rounded text-white text-xs flex items-center justify-center font-bold">
                            {i + 1}
                          </div>
                        ))}
                        <div className="w-8 h-12 bg-stone-100 rounded text-stone-400 text-xs flex items-center justify-center">+3</div>
                      </div>
                    </div>
                    
                    {/* Stories Preview */}
                    <div className="bg-white rounded-lg p-3 border border-pink-100">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageCircle size={14} className="text-pink-500" />
                        <span className="text-xs font-medium">Stories ({formatMixContent.channels?.instagram?.stories?.length || 4})</span>
                      </div>
                      <div className="flex gap-1">
                        {formatMixContent.channels?.instagram?.stories?.map((s, i) => (
                          <div key={i} className="w-6 h-10 bg-gradient-to-b from-purple-400 to-pink-400 rounded-full" />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <button className="w-full mt-3 px-3 py-2 bg-pink-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-pink-600">
                    <CheckCircle size={14} /> Bereit zum Posten
                  </button>
                </div>
                
                {/* Facebook */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Facebook size={20} className="text-blue-600" />
                    <h3 className="font-bold text-stone-900">FACEBOOK</h3>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Video+Carousel</span>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Video Preview */}
                    <div className="bg-white rounded-lg p-3 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Video size={14} className="text-blue-500" />
                        <span className="text-xs font-medium">Video (15s)</span>
                      </div>
                      <p className="text-xs text-stone-600 line-clamp-2">
                        {formatMixContent.channels?.facebook?.video?.script?.substring(0, 80)}...
                      </p>
                    </div>
                    
                    {/* Carousel Preview */}
                    <div className="bg-white rounded-lg p-3 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Image size={14} className="text-blue-500" />
                        <span className="text-xs font-medium">Carousel</span>
                      </div>
                      <div className="flex gap-1">
                        {formatMixContent.channels?.facebook?.carousel?.slice(0, 5).map((s, i) => (
                          <div key={i} className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-400 rounded text-white text-xs flex items-center justify-center font-bold">
                            {i + 1}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <button className="w-full mt-3 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-blue-600">
                    <CheckCircle size={14} /> Bereit zum Posten
                  </button>
                </div>
                
                {/* Google My Business */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg">📊</span>
                    <h3 className="font-bold text-stone-900">GMB</h3>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Infografik</span>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-green-100">
                    <div className="aspect-square bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg flex items-center justify-center mb-2">
                      <div className="text-center p-3">
                        <p className="font-bold text-orange-800 text-sm">{formatMixContent.channels?.gmb?.infographic?.title}</p>
                        <div className="mt-2 text-xs text-stone-600">
                          {formatMixContent.channels?.gmb?.infographic?.facts?.slice(0, 2).map((f, i) => (
                            <p key={i}>✅ {f}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 line-clamp-2">
                      {formatMixContent.channels?.gmb?.post_text?.substring(0, 60)}...
                    </p>
                  </div>
                  
                  <button className="w-full mt-3 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-green-600">
                    <CheckCircle size={14} /> Bereit zum Posten
                  </button>
                </div>
                
                {/* LinkedIn */}
                <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-4 border border-sky-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Linkedin size={20} className="text-sky-700" />
                    <h3 className="font-bold text-stone-900">LINKEDIN</h3>
                    <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded">Text+Carousel</span>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-sky-100">
                    <p className="text-xs text-stone-700 whitespace-pre-line line-clamp-6">
                      {formatMixContent.channels?.linkedin?.text_post?.substring(0, 200)}...
                    </p>
                    <div className="flex gap-1 mt-2">
                      {formatMixContent.channels?.linkedin?.carousel?.slice(0, 5).map((s, i) => (
                        <div key={i} className="w-6 h-6 bg-gradient-to-br from-sky-400 to-blue-400 rounded text-white text-xs flex items-center justify-center font-bold">
                          {i + 1}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <button className="w-full mt-3 px-3 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-sky-700">
                    <CheckCircle size={14} /> Bereit zum Posten
                  </button>
                </div>
              </div>
              
              {/* CTA Domain */}
              <div className="mt-6 p-4 bg-orange-50 rounded-xl border border-orange-200 text-center">
                <p className="text-sm text-stone-600">Alle Posts verlinken auf:</p>
                <p className="font-bold text-orange-600 text-lg">foerderfuchs-bildung.de/coaching</p>
              </div>
            </div>
            
            <div className="p-6 border-t border-stone-200 flex justify-between items-center sticky bottom-0 bg-white">
              <div className="text-sm text-stone-500">
                {formatMixContent.export?.total_assets || 12} Assets generiert • ZIP-Export bereit
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(formatMixContent, null, 2));
                    toast.success('Format-Mix JSON kopiert!');
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Copy size={16} />
                  JSON kopieren
                </button>
                <button
                  onClick={() => toast.info('ZIP-Export kommt bald!')}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Download size={16} />
                  ZIP Export
                </button>
                <button
                  onClick={() => toast.info('Multi-Post kommt bald!')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2"
                >
                  <Send size={16} />
                  POST ALL 4 KANÄLE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatenbasisPage;
